import styled from '@emotion/styled'
import { isMobile } from '@const/device'
import { BottomWrapper } from '../ScormViewer/styled'

type OptionRowStateProps = {
  $isAnswered: boolean
  $isSelected: boolean
  $isCorrectSelected: boolean
  $isWrongSelected: boolean
  $isCorrectMissed: boolean
}

type OptionInputProps = {
  $status: 'default' | 'selected' | 'success' | 'wrong'
}

type FeedbackBadgeProps = {
  $isSuccess: boolean
}

type LayoutProps = {
  $inModal: boolean
}

export const Wrapper = styled.div<{ $inModal: boolean }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: ${({ $inModal }) => ($inModal ? '100vh' : 'calc(100vh - 50px)')};
  margin: 0 auto;
  padding: ${isMobile ? '16px' : '0'};
  padding-bottom: ${({ $inModal }) => ($inModal ? '0' : '10px')};
  background: ${({ $inModal }) => ($inModal ? '#ffffff' : 'none')};
  gap: 10px;
  justify-content: center;
  ${({ $inModal }) =>
    $inModal
      ? 'position: fixed; left: 0; top: 0; z-index: 1000;'
      : ''}
`

export const ContentArea = styled.div<LayoutProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  width: ${({ $inModal }) => ($inModal ? 'calc(100vw - 150px)' : '78vw')};
  height: auto;
  min-height: 0;
  margin: 0 auto;
  gap: 16px;
`

export const Header = styled.div<LayoutProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  button {
    min-width: auto;
    height: 40px;
    padding: 12px;
    border: 1px solid #1d4e89;
    border-radius: 24px;
    color: #000000;
    background: transparent;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
`

export const PageTitle = styled.h1`
  margin: 16px 0 0;
  color: ${({ theme }) => theme.colors.text.black};
  font-size: ${isMobile ? '20px' : '28px'};
  line-height: ${isMobile ? '26px' : '34px'};
  font-weight: 700;
  letter-spacing: 0;
`

export const Card = styled.div<LayoutProps>`
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 32px;
  padding: ${isMobile ? '22px 16px 24px' : '48px 64px 48px 80px'};
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  height: auto;
  max-height: calc(100vh - 150px);
  min-height: 0;
  overflow-y: auto;
  color: ${({ theme }) => theme.colors.text.black};
  box-shadow: ${({ $inModal }) =>
    $inModal ? 'none' : '5px 5px 20px rgba(29, 78, 137, 0.1)'};
`

export const QuestionText = styled.p`
  margin: 0;
  width: 100%;
  font-size: ${isMobile ? '13px' : '24px'};
  line-height: ${isMobile ? '22px' : '36px'};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.black};
  white-space: pre-wrap;
`

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

export const OptionShell = styled.div`
  position: relative;
  display: flex;
  width: 100%;
`

export const OptionRow = styled.label<OptionRowStateProps>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  height: ${isMobile ? 'auto' : '88px'};
  min-height: ${isMobile ? '48px' : '88px'};
  padding: ${isMobile ? '10px 14px' : '24px'};
  border: 1px solid #f4f4f4;
  border-radius: 24px;
  cursor: ${({ $isAnswered }) => ($isAnswered ? 'default' : 'pointer')};
  user-select: none;
  transition:
    border-color 0.2s,
    background-color 0.2s;

  background-color: ${({ $isCorrectSelected, $isWrongSelected, $isCorrectMissed }) =>
    $isCorrectSelected
      ? '#e9fce7'
      : $isWrongSelected
      ? '#fff0f1'
      : $isCorrectMissed
      ? '#f5f7fa'
      : '#fafafa'};

  border-color: ${({ $isCorrectSelected, $isWrongSelected, $isCorrectMissed, $isSelected, $isAnswered }) =>
    $isCorrectSelected
      ? '#bbf8b5'
      : $isWrongSelected
      ? '#ffc7cd'
      : $isCorrectMissed
      ? '#bbf8b5'
      : $isSelected && !$isAnswered
      ? '#7b7b7b'
      : '#f4f4f4'};

  ${({ $isAnswered, $isCorrectSelected, $isWrongSelected, $isCorrectMissed }) =>
    !$isAnswered && !$isCorrectSelected && !$isWrongSelected && !$isCorrectMissed
      ? `
          &:hover {
            border-color: #f4f4f4;
            background-color: #fafafa;
          }
        `
      : ''}
`

export const OptionInput = styled.input<OptionInputProps>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${isMobile ? '18px' : '40px'};
  min-height: ${isMobile ? '18px' : '40px'};
  width: ${isMobile ? '18px' : '40px'};
  height: ${isMobile ? '18px' : '40px'};
  margin: 0;
  border: 1px solid
    ${({ $status, theme }) =>
      $status === 'wrong'
        ? theme.colors.error
        : $status === 'success'
        ? theme.colors.success
        : '#7b7b7b'};
  background: #ffffff;
  cursor: pointer;
  flex-shrink: 0;

  &[type='radio'] {
    border-radius: 50%;
  }

  &[type='checkbox'] {
    border-radius: 4px;
  }

  &:checked {
    border-color: ${({ $status, theme }) =>
      $status === 'wrong'
        ? theme.colors.error
        : $status === 'success'
        ? theme.colors.success
        : '#7b7b7b'};
  }

  &[type='radio']:checked::after {
    content: '';
    width: ${isMobile ? '8px' : '20px'};
    height: ${isMobile ? '8px' : '20px'};
    border-radius: 50%;
    background: ${({ $status, theme }) =>
      $status === 'wrong'
        ? theme.colors.error
        : $status === 'success'
        ? theme.colors.success
        : '#7b7b7b'};
  }

  &[type='checkbox']:checked {
    background: ${({ $status, theme }) =>
      $status === 'wrong'
        ? theme.colors.error
        : $status === 'success'
        ? theme.colors.success
        : theme.colors.main};
  }

  &[type='checkbox']:checked::after {
    content: '✓';
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
  }

  &:disabled {
    cursor: default;
  }
`

export const OptionText = styled.span`
  font-size: ${isMobile ? '11px' : '24px'};
  font-weight: 400;
  line-height: ${isMobile ? '19px' : '36px'};
  color: ${({ theme }) => theme.colors.text.black};
`

export const AnswerStateIcon = styled.span<{ $isVisible: boolean; $isCorrect: boolean }>`
  position: absolute;
  left: -38px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  color: #ffffff;
  background: ${({ $isCorrect, theme }) =>
    $isCorrect ? theme.colors.success : theme.colors.error};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};

  @media (max-width: 700px) {
    left: auto;
    right: 14px;
    top: 14px;
    transform: none;
  }
`

export const PageBottomRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-left: 0;
  padding-top: 0;

  > span {
    flex: 0 0 auto;
    font-size: 14px;
    line-height: 16px;
    font-weight: 400;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #7b7b7b;
  }

  @media (max-width: 700px) {
    margin-left: 0;
    flex-wrap: wrap;
    justify-content: flex-start;

    > span {
      width: 100%;
      flex: none;
      order: 1;
    }

  }
`

export const ActionSlot = styled.div`
  margin-left: auto;

  .ant-spin-nested-loading,
  .ant-spin-container {
    display: flex;
  }

  button {
    min-width: auto;
    min-height: 40px;
    height: 40px;
    padding: 12px;
    border-radius: 30px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  button:disabled {
    border: 1px solid #c4c4c4;
    background: #c4c4c4;
    color: #ffffff;

    &:hover {
      border: 1px solid #c4c4c4;
      background: #c4c4c4;
      color: #ffffff;
    }
  }

  @media (max-width: 700px) {
    order: 3;
    margin-left: auto;
  }
`

export const FeedbackBadge = styled.div<FeedbackBadgeProps>`
  position: absolute;
  left: 50%;
  top: -20px;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border-radius: 20px;
  padding: 18px 24px;
  min-width: 218px;
  min-height: 64px;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  color: #000000;
  background: ${({ $isSuccess }) => ($isSuccess ? '#9cf593' : '#ff989c')};
  box-shadow: 0 8px 18px rgba(29, 78, 137, 0.14);
  white-space: nowrap;

  @media (max-width: 700px) {
    position: static;
    transform: none;
  }
`

export const FeedbackIcon = styled.span<FeedbackBadgeProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  color: #ffffff;
  background: ${({ $isSuccess, theme }) =>
    $isSuccess ? theme.colors.success : theme.colors.error};
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
`

export const BottomControls = styled(BottomWrapper)`
  align-items: center;
  width: 78vw;
  margin: 0 auto;

  > div {
    gap: 24px;
  }

  button {
    min-width: auto;
    height: 40px;
    padding: 12px;
    border: 1px solid #7b7b7b;
    border-radius: 24px;
    color: #000000;
    background: transparent;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  @media (max-width: 700px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
    width: 100%;

    > div {
      justify-content: flex-start;
    }
  }
`
